import {
    Box,
    Checkbox,
    Chip,
    ClickAwayListener,
    Paper,
    Popper,
    TextField,
    Typography,
} from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function buildTree(options = []) {
    const seen = new Set();
    const unique = options.filter((s) => {
        if (!s || s.id === null || s.id === undefined) return false;
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
    });

    const itemById = {};
    unique.forEach((s) => {
        itemById[String(s.id)] = s;
    });

    const childrenByParentId = {};
    const roots = [];
    const expandableIds = new Set();

    unique.forEach((s) => {
        const parentKey = s.parentId === null || s.parentId === undefined ? null : String(s.parentId);
        const isRoot = parentKey === null || !itemById[parentKey] || String(s.id) === parentKey;

        if (isRoot) {
            roots.push(s);
        } else {
            if (!childrenByParentId[parentKey]) childrenByParentId[parentKey] = [];
            childrenByParentId[parentKey].push(s);
            expandableIds.add(parentKey);
        }
    });

    return { roots, itemById, childrenByParentId, expandableIds };
}

export default function TreeSelect({
    value,
    onChange,
    options,
    label = "Store",
    labelId = "store-tree-select-label",
    size = "small",
    fullWidth = true,
    includeAllOption = false,
    multiple = false,
    cascadeOnParentSelect = true,
    allowParentSelection = true,
    readOnly = false,
}) {
    const anchorRef = useRef(null);

    const { roots, itemById, childrenByParentId, expandableIds } = useMemo(
        () => buildTree(options),
        [options]
    );

    const descendantsById = useMemo(() => {
        const memo = {};

        const collect = (id) => {
            const key = String(id);
            if (memo[key]) return memo[key];

            const children = childrenByParentId[key] || [];
            const result = [];

            for (let i = 0; i < children.length; i += 1) {
                const childKey = String(children[i].id);
                result.push(childKey);
                result.push(...collect(childKey));
            }

            memo[key] = result;
            return result;
        };

        Object.keys(childrenByParentId).forEach((id) => {
            collect(id);
        });

        return memo;
    }, [childrenByParentId]);

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [expandedIds, setExpandedIds] = useState(() => new Set(expandableIds));

    useEffect(() => {
        setExpandedIds(new Set(expandableIds));
    }, [expandableIds]);

    const selectedIds = useMemo(() => {
        if (multiple) {
            const arr = Array.isArray(value) ? value : value ? [value] : [];
            return arr.map(String);
        }
        if (value === "" || value === null || value === undefined) return [];
        return [String(value)];
    }, [multiple, value]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const toggleExpanded = useCallback((id) => {
        const key = String(id);
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const query = inputValue.trim().toLowerCase();
    const searching = query.length > 0;

    const { visibleRoots, searchExpandedIds } = useMemo(() => {
        const nodeMatches = (node) => {
            const name = String(node?.name ?? "").toLowerCase();
            const id = String(node?.id ?? "").toLowerCase();
            return name.includes(query) || id.includes(query);
        };

        const visit = (node, path) => {
            const key = String(node.id);
            if (path.has(key)) return null;
            path.add(key);

            const rawChildren = childrenByParentId[key] || [];
            const children = rawChildren.map((c) => visit(c, path)).filter(Boolean);

            path.delete(key);

            if (searching && !nodeMatches(node) && children.length === 0) return null;

            return {
                node,
                key,
                hasChildren: rawChildren.length > 0,
                children,
            };
        };

        const rootsFiltered = roots.map((r) => visit(r, new Set())).filter(Boolean);
        const autoExpand = new Set();

        const collect = (n) => {
            if (n.hasChildren && n.children.length > 0) autoExpand.add(n.key);
            n.children.forEach(collect);
        };
        rootsFiltered.forEach(collect);

        return { visibleRoots: rootsFiltered, searchExpandedIds: autoExpand };
    }, [roots, childrenByParentId, searching, query]);

    const effectiveExpanded = searching ? searchExpandedIds : expandedIds;

    const emitValue = useCallback(
        (nextIds) => {
            if (multiple) {
                onChange?.({ target: { value: nextIds } });
            } else {
                onChange?.({ target: { value: nextIds[0] ?? "" } });
            }
        },
        [multiple, onChange]
    );

    const toggleSelect = useCallback(
        (id) => {
            const key = String(id);
            const hasChildren = (childrenByParentId[key] || []).length > 0;
            const parentSelectionBlocked = hasChildren && !allowParentSelection;

            if (parentSelectionBlocked) return;

            if (!multiple) {
                emitValue([key]);
                setOpen(false);
                setInputValue("");
                return;
            }

            const next = new Set(selectedSet);

            const idsToToggle =
                hasChildren && cascadeOnParentSelect
                    ? [key, ...(descendantsById[key] || [])]
                    : [key];

            const allSelected = idsToToggle.every((x) => next.has(x));
            if (allSelected) {
                idsToToggle.forEach((x) => next.delete(x));
            } else {
                idsToToggle.forEach((x) => next.add(x));
            }

            emitValue(Array.from(next));
        },
        [
            multiple,
            selectedSet,
            emitValue,
            childrenByParentId,
            allowParentSelection,
            cascadeOnParentSelect,
            descendantsById,
        ]
    );

    const selectedChips = useMemo(
        () =>
            selectedIds.map((id) => ({
                id,
                name: itemById[id]?.name ?? `#${id}`,
            })),
        [selectedIds, itemById]
    );

    const singleDisplay = useMemo(() => {
        if (value === "" && includeAllOption) return `All ${label}`;
        if (!selectedIds.length) return "";
        const id = selectedIds[0];
        return itemById[id]?.name ?? id;
    }, [includeAllOption, label, selectedIds, value, itemById]);

    const renderNode = (entry, depth = 0) => {
        const { node, key, hasChildren, children } = entry;
        const isExpanded = effectiveExpanded.has(key);
        const isChecked = selectedSet.has(key);
        const parentSelectionBlocked = hasChildren && !allowParentSelection;

        return (
            <TreeItem
                key={key}
                itemId={key}
                sx={{ "& .MuiTreeItem-iconContainer": { display: "none" } }}
                label={
                    <Box sx={{ display: "flex", alignItems: "center", py: 0.25 }}>
                        <Box
                            onMouseDown={(e) => {
                                if (!hasChildren) return;
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                if (!hasChildren || searching) return;
                                e.preventDefault();
                                e.stopPropagation();
                                toggleExpanded(key);
                            }}
                            sx={{
                                width: 18,
                                mr: 0.5,
                                color: hasChildren ? "text.secondary" : "transparent",
                                cursor: hasChildren && !searching ? "pointer" : "default",
                                userSelect: "none",
                            }}
                        >
                            {hasChildren ? (isExpanded ? "▾" : "▸") : "•"}
                        </Box>

                        {multiple && (
                            <Checkbox
                                size="small"
                                checked={isChecked}
                                disabled={parentSelectionBlocked}
                                onChange={() => toggleSelect(key)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0.5, mr: 1 }}
                            />
                        )}

                        <Box
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                if (parentSelectionBlocked) {
                                    if (!searching) toggleExpanded(key);
                                    return;
                                }

                                toggleSelect(key);
                            }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: 0,
                                opacity: parentSelectionBlocked ? 0.7 : 1,
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{ fontWeight: depth === 0 ? 700 : 400, mr: 0.5 }}
                            >
                                {node.name}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                                #{key}
                            </Typography>
                        </Box>
                    </Box>
                }
            >
                {children.map((child) => renderNode(child, depth + 1))}
            </TreeItem>
        );
    };

    return (
        <Box ref={anchorRef} sx={{ width: fullWidth ? "100%" : "auto" }}>
            <TextField
                id={labelId}
                size={size}
                fullWidth={fullWidth}
                label={label}
                value={multiple ? inputValue : open ? inputValue : singleDisplay}
                onClick={() => {
                    if (!readOnly) setOpen(true);
                }}
                onFocus={() => {
                    if (!readOnly) setOpen(true);
                }}
                onChange={(e) => {
                    if (!readOnly) setInputValue(e.target.value);
                }}
                InputProps={{
                    readOnly,
                    startAdornment:
                        multiple && selectedChips.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 1, maxHeight: 100, overflow: "auto" }}>
                                {selectedChips.map((chip) => (
                                    <Chip
                                        key={chip.id}
                                        size="small"
                                        label={chip.name}
                                        onDelete={
                                            readOnly
                                                ? undefined
                                                : () => {
                                                    const next = selectedIds.filter((x) => x !== chip.id);
                                                    emitValue(next);
                                                }
                                        }
                                        onMouseDown={(e) => e.stopPropagation()}
                                    />
                                ))}
                            </Box>
                        ) : undefined,
                }}
            />

            <Popper
                open={open && !readOnly}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                sx={{ zIndex: 1500, width: anchorRef.current?.clientWidth || 320 }}
            >
                <ClickAwayListener
                    onClickAway={() => {
                        setOpen(false);
                        setInputValue("");
                    }}
                >
                    <Paper elevation={8} sx={{ mt: 0.5, maxHeight: 360, overflow: "auto", p: 1 }}>
                        {includeAllOption && !multiple && (
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "action.hover" },
                                }}
                                onClick={() => {
                                    onChange?.({ target: { value: "" } });
                                    setOpen(false);
                                    setInputValue("");
                                }}
                            >
                                <Typography variant="body2">
                                    <em>All {label}</em>
                                </Typography>
                            </Box>
                        )}

                        <SimpleTreeView
                            disableSelection
                            expandedItems={Array.from(effectiveExpanded)}
                            onExpandedItemsChange={(_, ids) => {
                                if (searching) return;
                                setExpandedIds(new Set(ids.map(String)));
                            }}
                        >
                            {visibleRoots.map((root) => renderNode(root, 0))}
                        </SimpleTreeView>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}
